import {Fragment, useContext} from "react";
import {useLocation, useParams} from 'react-router-dom';
import {GlobalContext} from "../admin-frame/AdminFrame";
import {ApiUtility} from "../common/ApiUtility";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {useModelUpdateEngine} from "../common/hooks/useModelUpdateEngine";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {DispatchOrderForm} from "./DispatchOrderForm";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {LeaveWithoutSavingWarningUtility} from "../common/LeaveWithoutSavingWarningUtility";
import {Constants} from "../common/Constants";
import {DispatchOrder} from "../../types/DispatchOrder";
import {UrlUtility} from "../common/UrlUtility";

type UpdateDispatchOrderPageRouteParams = {
    dispatchOrderId: string;
}

interface UpdateDispatchOrderPageProps {
    showingNotificationModal: boolean;
}

export let UpdateDispatchOrderPage = function (props: UpdateDispatchOrderPageProps) {
    const context = useContext(GlobalContext);
    const params = useParams<UpdateDispatchOrderPageRouteParams>()
    const location = useLocation()
    const modelName = 'dispatch order';
    const urlBase = `${Constants.routes.dispatch.pathSegment}/dispatch-orders/${params.dispatchOrderId}`;
    const modelUpdateEngine = useModelUpdateEngine(
        ApiUtility.getDispatchOrder,
        ApiUtility.updateDispatchOrder,
        '' + params.dispatchOrderId,
        modelName,
        () => {
            modelUpdateEngine.refreshModel();
        },
        Constants.dispatchOrderMessageAdditionalConfig
    );

    function updateDispatchOrderStatus(
        statusId: number,
        disableNavigationBlocker: Function,
        notification?: any,
        cancellationReasonId?: number,
        cancellationText?: string
    ) {
        modelUpdateEngine.dataStore.updateAndRender({loading: true})
        let statusUpdateData: any = {
            id: params.dispatchOrderId,
            dispatch_order_status_id: statusId,
        }
        if (cancellationReasonId) {
            statusUpdateData.dispatch_order_cancellation_reason_id = cancellationReasonId
        }
        if (cancellationText) {
            statusUpdateData.dispatch_order_cancellation_text = cancellationText
        }
        if (notification) {
            statusUpdateData.notification = notification
        }
        ApiUtility.updateDispatchOrderStatus(statusUpdateData)
            .then(function () {
                disableNavigationBlocker()
                LeaveWithoutSavingWarningUtility.disableLeaveWithoutSavingWarnings();
                window.setTimeout(function () {
                    let message = `Successfully updated ${modelName} status`
                    if (notification) {
                        message += ' and sent notification.'
                    } else {
                        message += '.'
                    }
                    MessageDisplayerUtility.success(message, Constants.dispatchOrderMessageAdditionalConfig);
                    context.navigator.replace(urlBase)
                    window.setTimeout(() => {
                        modelUpdateEngine.dataStore.updateAndRender({model: null})
                        window.setTimeout(() => {
                            modelUpdateEngine.refreshModel()
                        }, 0)
                    }, 0)
                }, 0)
            })
            .catch(function (error) {
                modelUpdateEngine.dataStore.updateAndRender({loading: false});
                console && console.error(error);
                MessageDisplayerUtility.error(
                    `An error occurred while updating the ${modelName} status.`,
                    Constants.dispatchOrderMessageAdditionalConfig
                );
            });
    }

    function sendDispatchOrderNotification(disableNavigationBlocker: Function, notification: any) {
        modelUpdateEngine.dataStore.updateAndRender({loading: true})
        ApiUtility.sendDispatchOrderNotificationWithoutUpdate('' + params.dispatchOrderId, notification)
            .then(function () {
                disableNavigationBlocker()
                LeaveWithoutSavingWarningUtility.disableLeaveWithoutSavingWarnings();
                window.setTimeout(function () {
                    let message = 'Successfully sent notification.'
                    MessageDisplayerUtility.success(message, Constants.dispatchOrderMessageAdditionalConfig);
                    context.navigator.replace(urlBase)
                    window.setTimeout(() => {
                        modelUpdateEngine.dataStore.updateAndRender({model: null})
                        window.setTimeout(() => {
                            modelUpdateEngine.refreshModel()
                        }, 0)
                    }, 0)
                }, 0)
            })
            .catch(function (error) {
                modelUpdateEngine.dataStore.updateAndRender({loading: false});
                console && console.error(error);
                MessageDisplayerUtility.error(
                    `An error occurred while sending the notification.`,
                    Constants.dispatchOrderMessageAdditionalConfig
                );
            });
    }

    function openNotificationModal(statusId?: number, statusName?: string) {
        let queryParams: any = {}
        if (!!statusId) {
            queryParams.statusId = statusId
        }
        if (!!statusName) {
            queryParams.statusName = statusName
        }
        context.navigator.navigateTo(`${urlBase}/notify${UrlUtility.getUrlQueryStringFromQueryParamsObject(queryParams)}`);
    }

    function closeNotificationModal() {
        context.navigator.goBack()
    }

    return (
        <Fragment>
            <ConditionalRenderer if={modelUpdateEngine.dataStore.data().loading} renderer={() => (
                <OverlayLoadingIndicator/>
            )}/>
            <ConditionalRenderer if={modelUpdateEngine.dataStore.data().model} renderer={() => (
                <DispatchOrderForm
                    cancel={() => {
                        const from = new URLSearchParams(location.search).get('from');
                        context.navigator.navigateTo(from ?? ('/' + Constants.routes.dispatch.pathSegment));
                    }}
                    save={modelUpdateEngine.updateModel}
                    updateDispatchOrderStatus={updateDispatchOrderStatus}
                    sendDispatchOrderNotification={sendDispatchOrderNotification}
                    openNotificationModal={openNotificationModal}
                    closeNotificationModal={closeNotificationModal}
                    showingNotificationModal={props.showingNotificationModal}
                    dispatchOrder={modelUpdateEngine.dataStore.data().model as unknown as DispatchOrder}
                    isNew={false}
                />
            )}/>
        </Fragment>
    );
};
